import { useQuery, useMutation, useQueryClient } from "react-query";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

/**
 * Notification ADMIN hooks (SOUND-523) — the internal config surface: event
 * routing + channel-agnostic template authoring + live preview. Internal-only
 * endpoints (mounted at /notification-admin). React Query; auth + CompanyHeader
 * from useAuth()/useEffectiveCompanyId(). Distinct from the Phase-2 per-user
 * notification hooks (useNotifications.js).
 */
const K_EVENTS = "notif-admin-events";
const K_TEMPLATES = "notif-admin-templates";

function useAuthCtx() {
  const { user } = useAuth();
  const companyId = useEffectiveCompanyId();
  const authUser = user ? { id: user.id, accessToken: user.accessToken } : null;
  return { authUser, companyId, ready: !!authUser?.accessToken && !!companyId };
}

// ── Events (routing catalog) ──
export function useNotificationEvents({ enabled = true } = {}) {
  const { authUser, companyId, ready } = useAuthCtx();
  return useQuery(
    K_EVENTS,
    async () => {
      const res = await makeApiRequest("get", apiRoutes.notificationAdminEvents, authUser, companyId);
      // The list endpoint returns a bare array (or {data:[...]}).
      const body = res.data;
      return Array.isArray(body) ? body : body?.data || [];
    },
    { enabled: enabled && ready, staleTime: 60_000, refetchOnWindowFocus: false }
  );
}

export function useUpdateNotificationEvent() {
  const { authUser, companyId } = useAuthCtx();
  const qc = useQueryClient();
  return useMutation(
    ({ eventKey, updates }) =>
      makeApiRequest("put", apiRoutes.notificationAdminEvent(eventKey), authUser, companyId, updates),
    { onSuccess: () => qc.invalidateQueries(K_EVENTS) }
  );
}

// ── Templates (per event × channel) ──
export function useNotificationTemplates({ eventKey, channel, enabled = true } = {}) {
  const { authUser, companyId, ready } = useAuthCtx();
  const params = {};
  if (eventKey) params.eventKey = eventKey;
  if (channel) params.channel = channel;
  return useQuery(
    [K_TEMPLATES, params],
    async () => {
      const res = await makeApiRequest(
        "get",
        apiRoutes.notificationAdminTemplates(params),
        authUser,
        companyId
      );
      const body = res.data;
      return Array.isArray(body) ? body : body?.data || [];
    },
    { enabled: enabled && ready, staleTime: 30_000, refetchOnWindowFocus: false }
  );
}

export function useNotificationTemplate(templateId, { enabled = true } = {}) {
  const { authUser, companyId, ready } = useAuthCtx();
  return useQuery(
    [K_TEMPLATES, "one", templateId],
    async () => {
      const res = await makeApiRequest(
        "get",
        apiRoutes.notificationAdminTemplate(templateId),
        authUser,
        companyId
      );
      return res.data?.data || res.data || null;
    },
    { enabled: enabled && ready && !!templateId, refetchOnWindowFocus: false }
  );
}

export function useCreateNotificationTemplate() {
  const { authUser, companyId } = useAuthCtx();
  const qc = useQueryClient();
  return useMutation(
    (body) =>
      makeApiRequest("post", apiRoutes.createNotificationAdminTemplate, authUser, companyId, body),
    { onSuccess: () => qc.invalidateQueries(K_TEMPLATES) }
  );
}

export function useUpdateNotificationTemplate() {
  const { authUser, companyId } = useAuthCtx();
  const qc = useQueryClient();
  return useMutation(
    ({ templateId, updates }) =>
      makeApiRequest("put", apiRoutes.notificationAdminTemplate(templateId), authUser, companyId, updates),
    { onSuccess: () => qc.invalidateQueries(K_TEMPLATES) }
  );
}

// ── Live preview — byte-accurate to what sends (POST render) ──
export function useNotificationTemplatePreview() {
  const { authUser, companyId } = useAuthCtx();
  return useMutation((body) =>
    makeApiRequest("post", apiRoutes.notificationAdminTemplatePreview, authUser, companyId, body).then(
      (res) => res.data?.text ?? res.data?.data?.text ?? ""
    )
  );
}
